import {LinkIcon, PhotographIcon} from '@heroicons/react/outline';
import {Image} from '@tiptap/extension-image';
import {Link} from '@tiptap/extension-link';
import {EditorContent, useEditor} from '@tiptap/react';
import {StarterKit} from '@tiptap/starter-kit';
import {useCallback, useEffect, useRef} from 'react';

import type {AfriexRichTextDocument} from '@afriex/core/domain/types';

import {getErrorMessage} from '@/utils/errorMessage';
import {showErrorMessage} from '@/utils/showErrorMessage';

// Must match business-web's RichTextViewer, or nodes written here are dropped there.
// v3 adds link `title` and image `width`/`height` attrs, which the server validator's allowlists reject.
const LinkWithoutTitle = Link.extend({
  addAttributes() {
    const attributes = {...this.parent?.()};
    delete attributes.title;
    return attributes;
  },
});

const ImageWithoutSize = Image.extend({
  addAttributes() {
    const attributes = {...this.parent?.()};
    delete attributes.width;
    delete attributes.height;
    return attributes;
  },
});

const extensions = [
  StarterKit.configure({link: false}),
  LinkWithoutTitle.configure({openOnClick: false}),
  ImageWithoutSize,
];

type ToolbarButtonProps = {
  label: string;
  active?: boolean;
  onClick: () => void;
  children: React.ReactNode;
};

const ToolbarButton = ({
  label,
  active,
  onClick,
  children,
}: ToolbarButtonProps) => (
  <button
    type="button"
    aria-label={label}
    title={label}
    onClick={onClick}
    className={`flex h-7 w-7 items-center justify-center rounded text-sm font-semibold ${
      active
        ? 'bg-gray-700 text-white'
        : 'text-gray-300 hover:bg-gray-800 hover:text-white'
    }`}
  >
    {children}
  </button>
);

type Props = {
  value?: AfriexRichTextDocument;
  onChange?: (value: AfriexRichTextDocument) => void;
  editable?: boolean;
  /** Enables the image button; resolves to the uploaded image's URL. */
  onUploadImage?: (file: File) => Promise<string>;
  /** `field` is the dark form input; `plain` renders the content alone. */
  variant?: 'field' | 'plain';
  /** Accessible name for the editable field. */
  ariaLabel?: string;
  className?: string;
};

const RichTextEditor = ({
  value,
  onChange,
  editable = true,
  onUploadImage,
  variant = 'field',
  ariaLabel = 'Rich text editor',
  className = '',
}: Props) => {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const editor = useEditor({
    extensions,
    content: value,
    editable,
    // v3 stops re-rendering on every transaction; the toolbar's active state needs it.
    shouldRerenderOnTransaction: true,
    editorProps: {
      attributes: editable ? {'aria-label': ariaLabel} : {role: 'article'},
    },
    onUpdate: ({editor: e}) =>
      onChange?.(e.getJSON() as AfriexRichTextDocument),
  });

  // Follow a value set from outside (a loaded item, or a read-only preview).
  useEffect(() => {
    if (
      editor &&
      value &&
      JSON.stringify(editor.getJSON()) !== JSON.stringify(value)
    ) {
      editor.commands.setContent(value, {emitUpdate: false});
    }
  }, [editor, value]);

  const handleImageButtonClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleFileSelected = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      event.target.value = '';
      if (!file || !editor || !onUploadImage) {
        return;
      }
      try {
        const url = await onUploadImage(file);
        editor.chain().focus().setImage({src: url}).run();
      } catch (e) {
        showErrorMessage(
          `Image upload failed: ${getErrorMessage(e, 'unknown error')}`,
        );
      }
    },
    [editor, onUploadImage],
  );

  const handleLinkClick = useCallback(() => {
    if (!editor) {
      return;
    }
    const previousUrl = editor.getAttributes('link').href as string | undefined;
    const url = window.prompt('Link URL', previousUrl ?? 'https://');
    if (url === null) {
      return;
    }
    if (url === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
      return;
    }
    editor.chain().focus().extendMarkRange('link').setLink({href: url}).run();
  }, [editor]);

  if (!editor) {
    return null;
  }

  return (
    <div
      className={
        variant === 'field'
          ? 'rounded-md border border-gray-700 bg-gray-900'
          : ''
      }
    >
      {editable && (
        <div className="flex items-center gap-1 border-b border-gray-700 p-2">
          <ToolbarButton
            label="Bold"
            active={editor.isActive('bold')}
            onClick={() => editor.chain().focus().toggleBold().run()}
          >
            B
          </ToolbarButton>
          <ToolbarButton
            label="Italic"
            active={editor.isActive('italic')}
            onClick={() => editor.chain().focus().toggleItalic().run()}
          >
            <span className="italic">I</span>
          </ToolbarButton>
          <ToolbarButton
            label="Underline"
            active={editor.isActive('underline')}
            onClick={() => editor.chain().focus().toggleUnderline().run()}
          >
            <span className="underline">U</span>
          </ToolbarButton>
          <div className="mx-1 h-4 w-px bg-gray-700" />
          <ToolbarButton
            label="Link"
            active={editor.isActive('link')}
            onClick={handleLinkClick}
          >
            <LinkIcon className="h-4 w-4" />
          </ToolbarButton>
          {onUploadImage && (
            <ToolbarButton label="Image" onClick={handleImageButtonClick}>
              <PhotographIcon className="h-4 w-4" />
            </ToolbarButton>
          )}
          <ToolbarButton
            label="Bullet list"
            active={editor.isActive('bulletList')}
            onClick={() => editor.chain().focus().toggleBulletList().run()}
          >
            ≡
          </ToolbarButton>
          <ToolbarButton
            label="Heading"
            active={editor.isActive('heading', {level: 3})}
            onClick={() =>
              editor.chain().focus().toggleHeading({level: 3}).run()
            }
          >
            H
          </ToolbarButton>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={handleFileSelected}
          />
        </div>
      )}
      <EditorContent
        editor={editor}
        className={`prose prose-sm max-w-none [&_.ProseMirror]:outline-none ${
          variant === 'field'
            ? 'prose-invert px-4 py-3 text-gray-100 [&_.ProseMirror]:min-h-[160px]'
            : ''
        } ${className}`}
      />
    </div>
  );
};

export default RichTextEditor;
